/**
 * Component | Modal
 * Attribute-driven modal system with overlay backdrop
 */

import { IModule, component } from "@sygnal/sse";

@component('modal')
export class ModalComponent implements IModule {
  private elem: HTMLElement;
  private modals: HTMLElement[] = [];
  private activeModal: HTMLElement | null = null;

  constructor(elem: HTMLElement) {
    this.elem = elem;
  }

  setup(): void {
  }

  async exec(): Promise<void> {
    // Collect all [modal] child elements
    this.modals = Array.from(
      this.elem.querySelectorAll('[modal]')
    ) as HTMLElement[];

    // Apply overlay backdrop styles
    this.elem.style.position = 'fixed';
    this.elem.style.top = '0';
    this.elem.style.left = '0';
    this.elem.style.width = '100%';
    this.elem.style.height = '100%';
    this.elem.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    this.elem.style.zIndex = '1000';

    // Hide overlay and all modals initially
    this.elem.style.display = 'none';
    this.modals.forEach(modal => {
      modal.style.display = 'none';
    });

    this.bindEvents();
  }

  private bindEvents(): void {
    // Open: capture-phase delegation for [show-modal] triggers
    // Capture phase ensures this fires before decision-tree's bubbling handler
    document.addEventListener('click', (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest('[show-modal]');
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      const modalName = target.getAttribute('show-modal');
      if (modalName) {
        this.openModal(modalName);
      }
    }, true);

    // Close: overlay backdrop click
    this.elem.addEventListener('click', (event: MouseEvent) => {
      if (event.target === this.elem) {
        this.closeModal();
      }
    });

    // Close: [modal-close] button delegation
    this.elem.addEventListener('click', (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest('[modal-close]');

      if (target) {
        this.closeModal();
      }
    });
  }

  private openModal(name: string): void {
    const target = this.modals.find(
      modal => modal.getAttribute('modal') === name
    );
    if (!target) return;

    // Hide any currently active modal
    if (this.activeModal) {
      this.activeModal.style.display = 'none';
      this.clearPositioning(this.activeModal);
    }

    // Show overlay and target modal
    this.elem.style.display = '';
    target.style.display = '';
    this.applyPositioning(target);
    this.activeModal = target;
  }

  private closeModal(): void {
    if (this.activeModal) {
      this.activeModal.style.display = 'none';
      this.clearPositioning(this.activeModal);
      this.activeModal = null;
    }
    this.elem.style.display = 'none';
  }

  private applyPositioning(modal: HTMLElement): void {
    modal.style.position = 'fixed';
    modal.style.top = '50%';
    modal.style.left = '50%';
    modal.style.transform = 'translate(-50%, -50%)';
  }

  private clearPositioning(modal: HTMLElement): void {
    modal.style.position = '';
    modal.style.top = '';
    modal.style.left = '';
    modal.style.transform = '';
  }
}
